import React, { FC } from 'react'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { withStyles } from '@material-ui/core/styles'
import Switch from '@material-ui/core/Switch'
// @src imports
import { AccountSettingsInput, UserSettingsFragment } from 'src/graphql'
import { UiCard } from 'src/chrome'
import { Div, Select, Spacer, Text } from '@sendoutcards/quantum-design-ui'
import { useAccount, useActions, useMutations, useState } from 'src/hooks'

const StyledFormControlLabel = withStyles({
  label: {
    fontFamily: 'Montserrat',
    fontSize: '1em',
  },
})(FormControlLabel)

interface IEmailPreferencesProps {
  settings: UserSettingsFragment
  onToggle: (
    name: keyof Omit<AccountSettingsInput, 'defaultBackPanelId'>,
    value: boolean,
  ) => void
}
const defaultAdvanceDays = 21
const minimumDays = 8
const minimumDaysInternational = 15
const daysOptions = new Array(25).fill(null).map((_, i) => {
  return {
    label: `${i + 8}`,
    value: `${i + 8}`,
  }
})
const daysInternationalOptions = new Array(18).fill(null).map((_, i) => {
  return {
    label: `${i + 15}`,
    value: `${i + 15}`,
  }
})
const getInitialDaysValue = (index: number): number =>
  index >= 0 && index <= daysOptions.length
    ? index
    : defaultAdvanceDays - minimumDays

const EmailPreferences: FC<IEmailPreferencesProps> = props => {
  const { onToggle } = props
  const { defaultBackPanel, ...settings } = props.settings
  const {
    shouldReceivePromoEmails,
    shouldReceiveNotificationEmails,
    shouldReceiveReminderEmails,
    shouldPrintRecipientCompany,
  } = settings
  const mutations = useMutations()
  const actions = useActions()

  const { settings: persistedSettings } = useAccount()
  const remindersAdvanceNoticeDays =
    persistedSettings?.remindersAdvanceNoticeDays ?? defaultAdvanceDays
  const remindersAdvanceNoticeDaysIndex =
    remindersAdvanceNoticeDays - minimumDays
  const [selectedDays, setSelectedDays] = useState(
    getInitialDaysValue(remindersAdvanceNoticeDaysIndex),
  )

  const remindersAdvanceNoticeDaysInternational =
    persistedSettings?.remindersAdvanceNoticeDaysInternational ??
    defaultAdvanceDays
  const remindersAdvanceNoticeDaysInternationalIndex =
    remindersAdvanceNoticeDaysInternational - minimumDaysInternational
  const [selectedInternationalDays, setSelectedInternationalDays] = useState(
    getInitialDaysValue(remindersAdvanceNoticeDaysInternationalIndex),
  )

  const updateSelectedDays = async (
    days: string,
    isInternational: boolean = false,
  ) => {
    try {
      const remindersAdvanceNoticeDaysNumber = parseInt(days, 10)
      const {
        updateAccount: { account },
      } = await mutations.updateAccount({
        account: {
          settings: isInternational
            ? {
                remindersAdvanceNoticeDaysInternational: remindersAdvanceNoticeDaysNumber,
              }
            : {
                remindersAdvanceNoticeDays: remindersAdvanceNoticeDaysNumber,
              },
        },
      })
      actions.updatedAccount(account)
    } catch {
      console.log('Failed to update account')
    }
  }
  return (
    <UiCard title={'Communication Preferences'}>
      <div
        style={
          { display: 'flex', flexDirection: 'column' } as React.CSSProperties
        }
      >
        <Text type="largeBody" lineHeight={1.5}>
          U.S.A. Contacts:
        </Text>
        <Div
          display="flex"
          flexDirection="row"
          alignContent="center"
          alignItems="center"
        >
          <Div>
            <Select
              onChange={index => {
                setSelectedDays(index)
                updateSelectedDays(daysOptions[index].value)
              }}
              options={daysOptions}
              selectedOptionIndex={selectedDays}
              type="text"
              dropDownMaxHeight="200px"
              selectWidth="43px"
            />
          </Div>
          <Div>
            <Text type="body" outset={{ left: '5px' }} lineHeight={1.5}>
              days advance notice for Birthdays and Anniversaries
            </Text>
          </Div>
        </Div>
        <Spacer space="x1" />
        <Text type="largeBody" lineHeight={1.5}>
          International Contacts:
        </Text>
        <Div
          display="flex"
          flexDirection="row"
          alignContent="center"
          alignItems="center"
        >
          <Div>
            <Select
              onChange={index => {
                setSelectedInternationalDays(index)
                updateSelectedDays(daysInternationalOptions[index].value, true)
              }}
              options={daysInternationalOptions}
              selectedOptionIndex={selectedInternationalDays}
              type="text"
              dropDownMaxHeight="200px"
              selectWidth="43px"
            />
          </Div>
          <Div>
            <Text type="body" outset={{ left: '5px' }} lineHeight={1.5}>
              days advance notice for Birthdays and Anniversaries
            </Text>
          </Div>
        </Div>
        <Spacer space="x1" />
        <StyledFormControlLabel
          control={
            <Switch
              checked={shouldReceivePromoEmails}
              onChange={() => {
                onToggle('receivesPromoEmails', !shouldReceivePromoEmails)
              }}
              value=""
            />
          }
          label="Email me marketing and promotional information"
        />
        <Spacer space="x1" />
        <StyledFormControlLabel
          control={
            <Switch
              checked={shouldReceiveReminderEmails}
              onChange={() => {
                onToggle('receivesReminderEmails', !shouldReceiveReminderEmails)
              }}
              value=""
            />
          }
          label="Email me Anniversary and Birthday reminders"
        />
        <Spacer space="x1" />
        <StyledFormControlLabel
          control={
            <Switch
              checked={shouldReceiveNotificationEmails}
              onChange={() => {
                onToggle(
                  'receivesNotificationEmails',
                  !shouldReceiveNotificationEmails,
                )
              }}
              value=""
            />
          }
          label="Email me notifications for processing cards"
        />
        <Spacer space="x1" />
        <StyledFormControlLabel
          control={
            <Switch
              checked={shouldPrintRecipientCompany}
              onChange={() => {
                onToggle('printRecipientCompany', !shouldPrintRecipientCompany)
              }}
              value=""
            />
          }
          label="Print Recipient's company on the envelope"
        />
      </div>
    </UiCard>
  )
}

export default EmailPreferences
